import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Button, Flex, Icon, TextInput, useShowNotification } from '@inflearn/ds-react-next';
import { faAt } from '@inflearn/pro-regular-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { defaults, isEqual, trim } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';

import type {
  ClientApiV1AccountsMyProfileUpdateVariables,
  MyProfileResponse,
} from '../../../../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../../../../resources/ApiError';
import { logger } from '../../../../../../../resources/Logger';
import { useOpenEditCancelConfirmModal } from '../../../../../../hooks/useOpenEditCancelConfirmModal';
import useClientAccountMyProfileUpdateMutation from '../../../../../../mutations/useClientAccountMyProfileUpdateMutation';
import useClientAccountsMyProfileQuery from '../../../../../../queries/useClientAccountMyProfileQuery';
import { getAccountProfileQueryKey } from '../../../../../../queries/useClientAccountProfileQuery';
import { setQueryDataUpdater } from '../../../../../../utils/setQueryDataUpdater';
import MyProfileValidator from '../../../../../../validators/MyProfileValidator';
import Editor from '../../../../../common/Editor';
import ValidationMessage from '../../../../../common/ValidationMessage';
import { SettingRow } from '../../common/SettingRow';
import { SETTINGS_ROW_GAP, SettingSection } from '../../common/SettingSection';
import { DEFAULT_PROFILE_VALUES } from '../ProfileSection';
import { DEFAULT_IMAGE_URL, ProfileImage } from './ProfileImage';

type NonNullableProperties<T, K extends keyof T = never> = {
  [P in keyof T]: P extends K ? T[P] : NonNullable<T[P]>;
};

export type ProfileEditForm = NonNullableProperties<
  Omit<MyProfileResponse, 'id'>,
  'profileImageFileId'
>;

export const EditProfile = ({
  profile,
  onClose,
}: {
  profile: MyProfileResponse;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { showNotification } = useShowNotification();

  const form = useForm<ProfileEditForm>({
    mode: 'all',
    resolver: classValidatorResolver(MyProfileValidator),
    defaultValues: defaults(
      {
        profileImageFileId: profile.profileImageFileId,
        profileImageUrl: profile.profileImageUrl,
        nickname: profile.nickname,
        slug: profile.slug || '',
        introduce: profile.introduce || '',
      },
      DEFAULT_PROFILE_VALUES,
    ),
  });

  const queryClient = useQueryClient();
  const { mutate: profileUpdateMutate, isLoading } = useClientAccountMyProfileUpdateMutation({
    onError: (error) => {
      if (!ApiError.isApiError(error)) {
        logger.error(error);
        return;
      }

      if (isEqual(error.errorCode, 'SLUG_ALREADY_IN_USE')) {
        form.setError('slug', {
          message: t('이미 사용중인 주소입니다.'),
        });
      }
    },
  });

  const handleSubmit: SubmitHandler<MyProfileValidator> = (data) => {
    const variables: ClientApiV1AccountsMyProfileUpdateVariables = {
      profileImageFileId: data.profileImageFileId,
      nickname: trim(data.nickname),
      slug: trim(data.slug),
      introduce: trim(data.introduce),
    };

    profileUpdateMutate(variables, {
      onSuccess: () => {
        /** Patch 이후 즉시 GET 요청(invalidate)을 보냈을 때, 값이 변경되지 않는 문제가 있어 setQueryData 처리 */
        queryClient.setQueryData(
          useClientAccountsMyProfileQuery.queryKey,
          setQueryDataUpdater({
            ...variables,
            id: profile.id,
            profileImageUrl: variables.profileImageFileId
              ? form.getValues('profileImageUrl')
              : DEFAULT_IMAGE_URL,
          }),
        );

        // app-shell 헤더 프로필 이미지 업데이트
        queryClient.invalidateQueries({
          queryKey: getAccountProfileQueryKey(),
        });

        showNotification({
          title: t('저장이 완료되었습니다.'),
          type: 'success',
        });

        onClose();
      },
    });
  };

  return (
    <FormProvider {...form}>
      <Flex
        direction="column"
        gap={SETTINGS_ROW_GAP}
        component="form"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <SettingRow label={t('이미지')} align="flex-start" isEditMode>
          <Controller
            control={form.control}
            name="profileImageFileId"
            render={() => <ProfileImage />}
          />
        </SettingRow>
        <SettingRow label={t('닉네임')} align="flex-start" isEditMode isRequired>
          <Controller
            control={form.control}
            name="nickname"
            render={({ field, fieldState }) => {
              const error = fieldState.error?.message;

              return (
                <TextInput
                  size="md"
                  variant="default"
                  w="100%"
                  h={error ? undefined : 40}
                  placeholder={t('변경할 닉네임을 입력해 주세요.')}
                  error={error && <ValidationMessage message={error} />}
                  {...field}
                />
              );
            }}
          />
        </SettingRow>
        <SettingRow label={t('프로필 주소')} align="flex-start" isEditMode isRequired>
          <Controller
            control={form.control}
            name="slug"
            render={({ field, fieldState }) => {
              const error = fieldState.error?.message;

              return (
                <TextInput
                  size="md"
                  variant="default"
                  w="100%"
                  h={error ? undefined : 40}
                  placeholder={t('나만의 프로필 주소를 만들어보세요.')}
                  leftSection={<Icon icon={faAt} size="md" color="gray.9" />}
                  error={error && <ValidationMessage message={error} />}
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    const valueWithoutSpace = value.replace(/\s/g, '');

                    field.onChange({
                      target: {
                        value: valueWithoutSpace,
                      },
                    });
                  }}
                />
              );
            }}
          />
        </SettingRow>
        <SettingRow label={t('자기소개')} align="flex-start" isEditMode>
          <Controller
            control={form.control}
            name="introduce"
            render={({ field }) => (
              <Editor
                initialValue={profile.introduce ?? ''}
                autoFocus
                placeholder={t('나만의 스킬, 깃허브 링크 등으로 소개글을 채워보세요.')}
                {...field}
              />
            )}
          />
        </SettingRow>
        <SettingSection.TopRightSection gap={4}>
          <ButtonCancel onClose={onClose} />
          <Button type="submit" variant="filled" size="sm" disabled={isLoading}>
            {t('저장')}
          </Button>
        </SettingSection.TopRightSection>
      </Flex>
    </FormProvider>
  );
};

const ButtonCancel = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const form = useFormContext<ProfileEditForm>();
  const isDirty = form.formState.isDirty;
  const { openEditCancelConfirmModal } = useOpenEditCancelConfirmModal();

  const handleClose = () => {
    if (isDirty) {
      openEditCancelConfirmModal({ onConfirm: onClose });

      return;
    }

    onClose();
  };

  return (
    <Button variant="subtle" size="sm" color="gray" onClick={handleClose}>
      {t('취소')}
    </Button>
  );
};
